import React, { useEffect, useMemo, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Users, AlertTriangle, ArrowRight, Clock, TrendingUp, ClipboardCheck, FileText, UserPlus } from 'lucide-react';
import { getModuleIcon, MODULES } from '../../../content';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { PageLayout } from '../../shared/PageLayout';
import { StudentData } from './StudentDetailPanel';
import { DjayAvatar } from '../../../mascot';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { getAvatarGradient } from '../utils/avatarUtils';
import { needsAttention, STRUGGLING_THRESHOLD } from '../utils/studentUtils';
// Simple animated number component
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let startTime: number;
    const duration = 1000; // 1 second
    const startValue = 0;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutQuart
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(startValue + easeProgress * (value - startValue)));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
      }
    };
    requestAnimationFrame(step);
  }, [value]);
  return <>{displayValue}</>;
}
interface TodayPageProps {
  students: StudentData[];
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  onNavigate: (route: string) => void;
  classroomName?: string;
}
export function TodayPage({
  students,
  getModuleMastery,
  onSelectStudent,
  onNavigate,
  classroomName
}: TodayPageProps) {
  const stats = useMemo(() => {
    if (students.length === 0) return null;
    const strugglingStudents: Array<{
      student: StudentData;
      weakestModule: (typeof MODULES)[number];
      mastery: number;
    }> = [];
    let studentsPracticedThisWeek = 0;
    let totalMasterySum = 0;
    let totalMasteryCount = 0;
    const moduleMasterySums: Record<string, {
      sum: number;
      count: number;
    }> = {};
    MODULES.forEach(m => {
      moduleMasterySums[m.id] = {
        sum: 0,
        count: 0
      };
    });
    const inactiveStudents = students.filter(student => {
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      return weeklyActivity.filter(d => d.practiced).length === 0;
    }).map(student => {
      let lastActive = 0;
      if (student.data.questionStates) {
        for (const state of Object.values(student.data.questionStates)) {
          if (state.lastAttemptDate > lastActive) {
            lastActive = state.lastAttemptDate;
          }
        }
      }
      return {
        student,
        lastActive
      };
    }).sort((a, b) => b.lastActive - a.lastActive);
    students.forEach(student => {
      let lowestMastery = 100;
      let weakestModule = MODULES[0];
      let hasPracticed = false;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          hasPracticed = true;
          totalMasterySum += mastery;
          totalMasteryCount++;
          moduleMasterySums[module.id].sum += mastery;
          moduleMasterySums[module.id].count++;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
        }
      });
      if (hasPracticed && lowestMastery < STRUGGLING_THRESHOLD) {
        strugglingStudents.push({
          student,
          weakestModule,
          mastery: lowestMastery
        });
      }
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      if (weeklyActivity.some(d => d.practiced)) {
        studentsPracticedThisWeek++;
      }
    });
    const classAverageMastery = totalMasteryCount > 0 ? Math.round(totalMasterySum / totalMasteryCount) : 0;
    let strongestModule = MODULES[0];
    let weakestClassModule = MODULES[0];
    let highestAvg = -1;
    let lowestAvg = 101;
    MODULES.forEach(m => {
      const mStats = moduleMasterySums[m.id];
      if (mStats.count > 0) {
        const avg = mStats.sum / mStats.count;
        if (avg > highestAvg) {
          highestAvg = avg;
          strongestModule = m;
        }
        if (avg < lowestAvg) {
          lowestAvg = avg;
          weakestClassModule = m;
        }
      }
    });
    if (highestAvg === -1) {
      strongestModule = MODULES[0];
      weakestClassModule = MODULES[0];
    }
    return {
      strugglingStudents: strugglingStudents.sort((a, b) => a.mastery - b.mastery).slice(0, 6),
      totalStruggling: strugglingStudents.length,
      inactiveStudents,
      studentsPracticedThisWeek,
      classAverageMastery,
      strongestModule,
      weakestClassModule,
      highestAvg,
      lowestAvg
    };
  }, [students, getModuleMastery]);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut'
      }
    }
  };
  const listContainerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const listItemVariants = {
    hidden: {
      opacity: 0,
      x: -10
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }
  };
  const hour = new Date().getHours();
  let greeting = 'Goedenavond';
  if (hour >= 6 && hour < 12) greeting = 'Goedemorgen';else if (hour >= 12 && hour < 18) greeting = 'Goedemiddag';
  const dateString = new Date().toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  if (students.length === 0) {
    return <PageLayout title="Vandaag" description="Overzicht van vandaag" data-id="element-6195">
        <motion.div className="flex flex-col items-center justify-center py-20 px-4 text-center" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-6196">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-4" data-id="element-6197">
            <Users className="w-8 h-8" data-id="element-6198" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-6199">
            Nog geen leerlingen
          </h2>
          <p className="text-slate-500 max-w-md mb-6" data-id="element-6200">
            Je klas is nog leeg. Voeg leerlingen toe om hun voortgang te volgen
            en inzicht te krijgen in hun prestaties.
          </p>
          <div className="flex items-center gap-3" data-id="element-6201">
            <Button variant="primary" onClick={() => onNavigate('class')} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_4px_0_0_rgb(67,56,202)]" data-id="element-6202">
              Naar Mijn Klas
            </Button>
          </div>
        </motion.div>
      </PageLayout>;
  }
  if (!stats) return null;
  const StrongestIcon = getModuleIcon(stats.strongestModule.icon);
  const WeakestIcon = getModuleIcon(stats.weakestClassModule.icon);
  return <PageLayout title="Vandaag" description={classroomName ? `Actuele inzichten voor ${classroomName}` : 'Actuele inzichten en aandachtspunten'} data-id="element-6203">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-6204">
        {/* Welcome Block */}
        <motion.div variants={itemVariants} className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-6 text-white shadow-md relative overflow-hidden" data-id="element-6205">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-y-1/2 translate-x-1/3 blur-2xl" data-id="element-6206"></div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-6207">
            <div data-id="element-6208">
              <h1 className="text-3xl font-black mb-1" data-id="element-6209">{greeting}!</h1>
              <p className="text-indigo-100 font-medium text-lg" data-id="element-6210">
                {classroomName || 'Mijn Klas'} •{' '}
                <span className="capitalize" data-id="element-6211">{dateString}</span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm font-medium bg-white/10 px-4 py-2.5 rounded-xl backdrop-blur-sm border border-white/10" data-id="element-6212">
              <div className="flex items-center gap-1.5" data-id="element-6213">
                <Users className="w-4 h-4 text-indigo-200" data-id="element-6214" />
                <span data-id="element-6215">{students.length} leerlingen</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-6216"></div>
              <div className="flex items-center gap-1.5" data-id="element-6217">
                <TrendingUp className="w-4 h-4 text-emerald-300" data-id="element-6218" />
                <span data-id="element-6219">{stats.studentsPracticedThisWeek} actief</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-6220"></div>
              <div className="flex items-center gap-1.5" data-id="element-6221">
                <AlertTriangle className={`w-4 h-4 ${stats.totalStruggling > 0 ? 'text-rose-300' : 'text-indigo-200'}`} data-id="element-6222" />
                <span data-id="element-6223">{stats.totalStruggling} aandachtspunten</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions Row */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6224">
          <button onClick={() => onNavigate('assessments')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-6225">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors" data-id="element-6226">
              <ClipboardCheck className="w-5 h-5" data-id="element-6227" />
            </div>
            <div data-id="element-6228">
              <div className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors" data-id="element-6229">
                Nieuwe toets
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-6230">
                Toets inplannen
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('reports')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-6231">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors" data-id="element-6232">
              <FileText className="w-5 h-5" data-id="element-6233" />
            </div>
            <div data-id="element-6234">
              <div className="font-bold text-slate-900 group-hover:text-emerald-700 transition-colors" data-id="element-6235">
                Rapporten
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-6236">
                Resultaten bekijken
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('management')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-6237">
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition-colors" data-id="element-6238">
              <UserPlus className="w-5 h-5" data-id="element-6239" />
            </div>
            <div data-id="element-6240">
              <div className="font-bold text-slate-900 group-hover:text-amber-700 transition-colors" data-id="element-6241">
                Leerling toevoegen
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-6242">
                Klas beheren
              </div>
            </div>
          </button>
        </motion.div>

        {/* Class Overview Card */}
        <motion.div variants={itemVariants} data-id="element-6243">
          <Card className="bg-white border-slate-200 shadow-sm overflow-hidden p-5" data-id="element-6244">
            <div className="flex items-center gap-2 mb-4" data-id="element-6245">
              <TrendingUp className="w-5 h-5 text-indigo-600" data-id="element-6246" />
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider" data-id="element-6247">
                Klas Overzicht
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-6248">
              <div className="flex flex-col" data-id="element-6249">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-6250">
                  Gemiddelde beheersing
                </div>
                <div className="flex items-end gap-2" data-id="element-6251">
                  <div className="text-3xl font-black text-slate-900" data-id="element-6252">
                    <AnimatedNumber value={stats.classAverageMastery} data-id="element-6253" />%
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0" data-id="element-6254">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-6255">
                  Sterkste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-6256">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-6257">
                    <StrongestIcon className="w-4 h-4" data-id="element-6258" />
                  </div>
                  <div data-id="element-6259">
                    <div className="font-bold text-slate-900" data-id="element-6260">
                      {stats.strongestModule.title}
                    </div>
                    <div className="text-xs font-medium text-emerald-600" data-id="element-6261">
                      {stats.highestAvg > -1 ? Math.round(stats.highestAvg) : 0}
                      % beheersing
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0 cursor-pointer hover:bg-slate-50 rounded-xl p-2 -m-2 transition-colors" onClick={() => onNavigate('subjects')} data-id="element-6262">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-6263">
                  Zwakste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-6264">
                  <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center" data-id="element-6265">
                    <WeakestIcon className="w-4 h-4" data-id="element-6266" />
                  </div>
                  <div data-id="element-6267">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-6268">
                      {stats.weakestClassModule.title}
                    </div>
                    <div className="text-xs font-medium text-rose-600" data-id="element-6269">
                      {stats.lowestAvg < 101 ? Math.round(stats.lowestAvg) : 0}%
                      beheersing
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Attention Needed */}
        <motion.section variants={itemVariants} data-id="element-6270">
          <div className="flex items-center justify-between mb-4" data-id="element-6271">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-6272">
              <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-6273" />
              Aandachtspunten
            </h2>
          </div>

          {stats.strugglingStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-6274">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-6275">
                <div className="w-8" data-id="element-6276"></div>
                <div data-id="element-6277">Naam & Vak</div>
                <div className="text-right w-24" data-id="element-6278">Beheersing</div>
                <div className="w-6" data-id="element-6279"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-6280">
                {stats.strugglingStudents.map((item, i) => {
              const IconComp = getModuleIcon(item.weakestModule.icon);
              return <motion.div key={item.student.id} variants={listItemVariants} onClick={() => onSelectStudent(item.student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-6281">
                      <div className="relative" data-id="element-6282">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(item.student.name)} flex items-center justify-center text-white font-bold text-sm shadow-sm`} data-id="element-6283">
                          {item.student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-rose-500 shadow-sm" role="img" aria-label="Aandacht nodig" data-id="element-6284" />
                      </div>
                      <div className="min-w-0 flex items-center gap-2" data-id="element-6285">
                        <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-6286">
                          {item.student.name}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-6287">
                          <IconComp className="w-3 h-3 text-rose-500" data-id="element-6288" />
                          <span className="truncate font-medium" data-id="element-6289">
                            {item.weakestModule.title}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end w-24" data-id="element-6290">
                        <span className="font-black text-sm text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md" data-id="element-6291">
                          {item.mastery}%
                        </span>
                      </div>
                      <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-6292">
                        <ArrowRight className="w-4 h-4" data-id="element-6293" />
                      </div>
                    </motion.div>;
            })}
              </motion.div>
            </Card> : <Card className="p-8 text-center border-emerald-100 bg-emerald-50/50 shadow-sm overflow-hidden relative" data-id="element-6294">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(52,211,153,0.1)_0,transparent_100%)]" data-id="element-6295" />
              <motion.div className="flex justify-center mb-4 relative z-10" initial={{
            scale: 0.8,
            y: 20
          }} animate={{
            scale: 1,
            y: 0
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20,
            delay: 0.2
          }} data-id="element-6296">
                <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-6297">
                  <DjayAvatar size="md" emotion="happy" data-id="element-6298" />
                </motion.div>
              </motion.div>
              <h3 className="text-xl font-black text-emerald-900 mb-2 relative z-10" data-id="element-6299">
                Alles onder controle!
              </h3>
              <p className="text-emerald-700 font-medium relative z-10" data-id="element-6300">
                Er zijn momenteel geen leerlingen die structureel vastlopen.
                Goed bezig! 🎉
              </p>
            </Card>}
          {stats.totalStruggling > 6 && <div className="mt-4 text-center" data-id="element-6301">
              <button onClick={() => onNavigate('class')} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors inline-flex items-center gap-1.5 group" data-id="element-6302">
                Bekijk alle {stats.totalStruggling} leerlingen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-6303" />
              </button>
            </div>}
        </motion.section>

        {/* Inactive Students */}
        {stats.inactiveStudents.length > 0 && <motion.section variants={itemVariants} data-id="element-6304">
            <div className="flex items-center justify-between mb-4" data-id="element-6305">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-6306">
                <Clock className="w-4 h-4 text-amber-500" data-id="element-6307" />
                Niet geoefend deze week
              </h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-6308">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-6309">
                <div className="w-8" data-id="element-6310"></div>
                <div data-id="element-6311">Naam</div>
                <div className="text-right w-32" data-id="element-6312">Inactief sinds</div>
                <div className="w-6" data-id="element-6313"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-6314">
                {stats.inactiveStudents.slice(0, 5).map(({
              student,
              lastActive
            }, i) => {
              const daysAgo = lastActive > 0 ? Math.floor((Date.now() - lastActive) / (1000 * 60 * 60 * 24)) : -1;
              const inactiveLabel = daysAgo < 0 ? 'Nog nooit geoefend' : daysAgo === 0 ? 'Vandaag' : daysAgo === 1 ? 'Gisteren' : daysAgo < 7 ? `${daysAgo} dagen geleden` : new Date(lastActive).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'short'
              });
              return <motion.div key={student.id} variants={listItemVariants} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-6315">
                        <div className="relative" data-id="element-6316">
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm shadow-sm`} data-id="element-6317">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500 shadow-sm" role="img" aria-label="Inactief deze week" data-id="element-6318" />
                        </div>
                        <div className="min-w-0" data-id="element-6319">
                          <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-6320">
                            {student.name}
                          </div>
                        </div>
                        <div className="flex items-center justify-end w-32" data-id="element-6321">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${daysAgo < 0 ? 'bg-rose-50 text-rose-600' : daysAgo > 14 ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-6322">
                            {inactiveLabel}
                          </span>
                        </div>
                        <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-6323">
                          <ArrowRight className="w-4 h-4" data-id="element-6324" />
                        </div>
                      </motion.div>;
            })}
              </motion.div>
              {stats.inactiveStudents.length > 5 && <div className="p-3 bg-slate-50 border-t border-slate-100 text-center" data-id="element-6325">
                  <button onClick={() => onNavigate('class')} className="text-xs font-bold text-slate-500 hover:text-indigo-600 uppercase tracking-wider transition-colors inline-flex items-center gap-1 group" data-id="element-6326">
                    + {stats.inactiveStudents.length - 5} meer bekijken
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" data-id="element-6327" />
                  </button>
                </div>}
            </Card>
          </motion.section>}
      </motion.div>
    </PageLayout>;
}